/**
 * Chunk 190: AI Price Signal Analysis — API client
 */
import apiClient from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SignalType = 'support_resistance' | 'trend' | 'momentum' | 'on_chain' | 'sentiment';
export type SignalStrength = 'strong_buy' | 'buy' | 'neutral' | 'sell' | 'strong_sell';

export interface DataPoints {
  priceCount: number;
  currentPrice: number | null;
  sma20: number | null;
  sma50: number | null;
  sma200: number | null;
  priceVsSma200Pct: number | null;
  daysAboveSma50: number | null;
  rsi14: number | null;
  macdLine: number | null;
  macdSignal: number | null;
  macdHistogram: number | null;
  bbUpper: number | null;
  bbMiddle: number | null;
  bbLower: number | null;
  whaleSignal: 'accumulating' | 'neutral' | 'distributing' | null;
  topHolderConcentration: number | null;
  largeTransferCount24h: number | null;
  holderCount: number | null;
  aiModelUsed: string;
}

export interface PriceSignal {
  id: string;
  symbol: string;
  signalType: SignalType;
  signalStrength: SignalStrength;
  narrative: string;
  confidencePct: number;
  dataPoints: DataPoints;
  generatedAt: string;
  expiresAt: string;
}

export interface UserSignalsResponse {
  userId: string;
  signals: PriceSignal[];
  total: number;
  fetchedAt: string;
}

export interface SymbolSignalsResponse {
  symbol: string;
  signals: PriceSignal[];
  fetchedAt: string;
}

// ─── Labels ───────────────────────────────────────────────────────────────────

export const SIGNAL_TYPE_LABELS: Record<SignalType, string> = {
  support_resistance: 'Support / Resistance',
  trend: 'Trend',
  momentum: 'Momentum',
  on_chain: 'On-Chain',
  sentiment: 'Sentiment',
};

export const SIGNAL_STRENGTH_LABELS: Record<SignalStrength, string> = {
  strong_buy: 'Strong Buy',
  buy: 'Buy',
  neutral: 'Neutral',
  sell: 'Sell',
  strong_sell: 'Strong Sell',
};

export const SIGNAL_STRENGTH_COLORS: Record<SignalStrength, string> = {
  strong_buy: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
  buy: 'bg-emerald-500/10 text-emerald-400',
  neutral: 'bg-gray-700/50 text-gray-400',
  sell: 'bg-red-500/10 text-red-400',
  strong_sell: 'bg-red-500/20 text-red-400 border border-red-500/30',
};

export const SIGNAL_CARD_BORDERS: Record<SignalStrength, string> = {
  strong_buy: 'border border-emerald-500/40',
  buy: 'border border-gray-800',
  neutral: 'border border-gray-800',
  sell: 'border border-gray-800',
  strong_sell: 'border border-red-500/40',
};

export type SignalFilter = 'all' | 'bullish' | 'bearish' | 'neutral';

export function isBullish(strength: SignalStrength): boolean {
  return strength === 'strong_buy' || strength === 'buy';
}

export function isBearish(strength: SignalStrength): boolean {
  return strength === 'strong_sell' || strength === 'sell';
}

export function isStrongSignal(strength: SignalStrength): boolean {
  return strength === 'strong_buy' || strength === 'strong_sell';
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function getUserSignals(userId: string): Promise<UserSignalsResponse> {
  const res = await apiClient.get<UserSignalsResponse>(`/analytics/signals/${userId}`);
  return res.data;
}

export async function getSymbolSignals(symbol: string): Promise<SymbolSignalsResponse> {
  const res = await apiClient.get<SymbolSignalsResponse>(`/analytics/signals/symbol/${symbol}`);
  return res.data;
}

export async function refreshUserSignals(userId: string): Promise<UserSignalsResponse> {
  const res = await apiClient.post<UserSignalsResponse>(`/analytics/signals/${userId}/refresh`);
  return res.data;
}

/**
 * Count non-neutral signals from a list (used for sidebar badge).
 */
export function countNonNeutralSignals(signals: PriceSignal[]): number {
  return signals.filter((s) => s.signalStrength !== 'neutral').length;
}
